/**
 * Authentication store using Zustand
 * Manages authentication state and actions
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import authService, { User, SignUpRequest, SignInRequest, VerifyCodeRequest } from '../services/auth';

interface AuthStore {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitializing: boolean;
  error: string | null;
  debugPasscode: string | null;

  // Auth flow state
  pendingEmail: string | null; // Email waiting for code verification
  isVerifyingCode: boolean;

  // Actions
  signUp: (data: SignUpRequest) => Promise<boolean>;
  signIn: (data: SignInRequest) => Promise<boolean>;
  verifyCode: (data: VerifyCodeRequest) => Promise<boolean>;
  resendCode: (email: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  getCurrentUser: () => Promise<boolean>;
  updateProfile: (updates: Partial<User>) => Promise<boolean>;
  changeEmail: (newEmail: string) => Promise<boolean>;

  // Utility actions
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  initialize: () => Promise<void>;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isInitializing: true,
      error: null,
      pendingEmail: null,
      isVerifyingCode: false,
      debugPasscode: null,

      // Sign up new user
      signUp: async (data: SignUpRequest) => {
        set({ isLoading: true, error: null });

        try {
          const result = await authService.signUp(data);

          if (result.error) {
            set({ error: result.error, isLoading: false, debugPasscode: null });
            return false;
          }

          set({
            pendingEmail: data.email,
            isLoading: false,
            error: null,
            debugPasscode: result.data?.debugPasscode || null
          });

          return true;
        } catch (error: any) {
          set({
            error: error.message || 'Sign up failed',
            isLoading: false
          });
          return false;
        }
      },

      // Sign in existing user
      signIn: async (data: SignInRequest) => {
        set({ isLoading: true, error: null });

        try {
          const result = await authService.signIn(data);

          if (result.error) {
            set({ error: result.error, isLoading: false, debugPasscode: null });
            return false;
          }

          set({
            pendingEmail: data.email,
            isLoading: false,
            error: null,
            debugPasscode: result.data?.debugPasscode || null
          });

          return true;
        } catch (error: any) {
          set({
            error: error.message || 'Sign in failed',
            isLoading: false
          });
          return false;
        }
      },

      // Verify passcode
      verifyCode: async (data: VerifyCodeRequest) => {
        set({ isVerifyingCode: true, error: null });

        try {
          const result = await authService.verifyCode(data);

          if (result.error) {
            set({ error: result.error, isVerifyingCode: false });
            return false;
          }

          if (result.data) {
            set({
              user: result.data.user,
              isAuthenticated: true,
              isVerifyingCode: false,
              pendingEmail: null,
              error: null,
              debugPasscode: null
            });
            return true;
          }

          return false;
        } catch (error: any) {
          set({
            error: error.message || 'Code verification failed',
            isVerifyingCode: false
          });
          return false;
        }
      },

      // Resend verification code
      resendCode: async (email: string) => {
        set({ isLoading: true, error: null });

        try {
          const result = await authService.resendCode(email);

          if (result.error) {
            set({ error: result.error, isLoading: false, debugPasscode: null });
            return false;
          }

          set({
            isLoading: false,
            error: null,
            debugPasscode: result.data?.debugPasscode || null
          });
          return true;
        } catch (error: any) {
          set({
            error: error.message || 'Failed to resend code',
            isLoading: false,
            debugPasscode: null
          });
          return false;
        }
      },

      // Sign out user
      signOut: async () => {
        set({ isLoading: true });

        try {
          await authService.signOut();
        } catch (error) {
          console.error('Sign out error:', error);
        } finally {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            pendingEmail: null,
            isVerifyingCode: false,
            debugPasscode: null
          });
        }
      },

      // Refresh authentication token
      refreshToken: async () => {
        try {
          const result = await authService.refreshToken();

          if (result.error) {
            // Refresh failed, sign out
            get().signOut();
            return false;
          }

          if (result.data) {
            set({
              user: result.data.user,
              isAuthenticated: true,
              error: null,
              debugPasscode: null
            });
            return true;
          }

          return false;
        } catch (error) {
          get().signOut();
          return false;
        }
      },

      // Get current user data
      getCurrentUser: async () => {
        set({ isLoading: true });

        try {
          const result = await authService.getCurrentUser();

          if (result.error) {
            set({ error: result.error, isLoading: false });
            return false;
          }

          if (result.data) {
            set({
              user: result.data,
              isAuthenticated: true,
              isLoading: false,
              error: null,
              debugPasscode: null
            });
            return true;
          }

          return false;
        } catch (error: any) {
          set({
            error: error.message || 'Failed to get user data',
            isLoading: false
          });
          return false;
        }
      },

      // Update user profile
      updateProfile: async (updates: Partial<User>) => {
        set({ isLoading: true, error: null });

        try {
          const result = await authService.updateProfile(updates);

          if (result.error) {
            set({ error: result.error, isLoading: false });
            return false;
          }

          if (result.data) {
            set({
              user: result.data,
              isLoading: false,
              error: null,
              debugPasscode: null
            });
            return true;
          }

          return false;
        } catch (error: any) {
          set({
            error: error.message || 'Failed to update profile',
            isLoading: false
          });
          return false;
        }
      },

      // Change user email
      changeEmail: async (newEmail: string) => {
        set({ isLoading: true, error: null });

        try {
          const result = await authService.changeEmail(newEmail);

          if (result.error) {
            set({ error: result.error, isLoading: false });
            return false;
          }

          set({
            pendingEmail: newEmail,
            isLoading: false,
            error: null,
            debugPasscode: null
          });

          return true;
        } catch (error: any) {
          set({
            error: error.message || 'Failed to change email',
            isLoading: false
          });
          return false;
        }
      },

      // Clear error state
      clearError: () => {
        set({ error: null });
      },

      // Set loading state
      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      // Initialize authentication state
      initialize: async () => {
        set({ isInitializing: true });

        try {
          // Check if user is already authenticated
          if (authService.isAuthenticated()) {
            const user = authService.getUser();
            if (user) {
              set({
                user,
                isAuthenticated: true,
                isLoading: false,
                isInitializing: false,
                error: null,
                debugPasscode: null
              });

              // Setup axios interceptors
              authService.setupInterceptors();

              // Optionally refresh user data without blocking initialization
              get().getCurrentUser();
              return;
            }
          }

          // Not authenticated
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            isInitializing: false,
            error: null,
            debugPasscode: null
          });
        } catch (error: any) {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            isInitializing: false,
            error: error.message || 'Initialization failed',
            debugPasscode: null
          });
        }
      }
    }),
    {
      name: 'vc-research-auth',
      partialize: (state) => ({
        // Only persist essential state
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        pendingEmail: state.pendingEmail
      })
    }
  )
);

// Hook for easy access to auth actions
export const useAuthActions = () => {
  const {
    signUp,
    signIn,
    verifyCode,
    resendCode,
    signOut,
    refreshToken,
    getCurrentUser,
    updateProfile,
    changeEmail,
    clearError,
    setLoading,
    initialize
  } = useAuthStore();

  return {
    signUp,
    signIn,
    verifyCode,
    resendCode,
    signOut,
    refreshToken,
    getCurrentUser,
    updateProfile,
    changeEmail,
    clearError,
    setLoading,
    initialize
  };
};
